import type { ThreadId } from "@cmmd-forge/contracts";
import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import type { TerminalContextDraft } from "../../lib/terminalContext";
import {
  drainGenerationFor,
  type QueuedComposerMessage,
  useQueuedMessageStore,
} from "../../queuedMessageStore";
import { describeSendFailure } from "../../rpc/transportError";
import type { ChatTurnSubmissionInput } from "./chatTurnSubmissionTypes";
import { decideQueuedTurnGate } from "./queuedTurnGate";

interface PreparedComposerSend {
  readonly prompt: string;
  readonly images: ComposerImageAttachment[];
  readonly files: ComposerFileAttachment[];
  readonly terminalContexts: TerminalContextDraft[];
  readonly restoreFailedQueueEntry: (threadId: ThreadId, error: unknown) => boolean;
}

/** Takes a queued snapshot or captures the draft, queueing it when a running turn owns send. */
export function prepareComposerTurnSend<ComposerTarget>(
  input: ChatTurnSubmissionInput<ComposerTarget>,
  queued?: QueuedComposerMessage,
): PreparedComposerSend | null {
  return queued ? prepareQueuedSend(input, queued) : prepareDraftSend(input);
}

/** Claims a queue entry and keeps its drain generation for failed dispatch restoration. */
function prepareQueuedSend<ComposerTarget>(
  input: ChatTurnSubmissionInput<ComposerTarget>,
  queued: QueuedComposerMessage,
): PreparedComposerSend | null {
  if (input.isConnecting || input.sendInFlightRef.current) return null;
  const queuedDrainGeneration = input.queueContext
    ? drainGenerationFor(input.queueContext.threadKey)
    : null;
  const taken = input.queueContext
    ? useQueuedMessageStore
        .getState()
        .take(input.queueContext.threadKey, queued.id, input.queueContext.latestToolActivityId)
    : queued;
  if (!taken) return null;
  return {
    prompt: taken.prompt,
    images: [...taken.images],
    files: [...taken.files],
    terminalContexts: [...taken.terminalContexts],
    restoreFailedQueueEntry: (threadId, error) => {
      if (!input.queueContext) return false;
      if (drainGenerationFor(input.queueContext.threadKey) !== queuedDrainGeneration) return true;
      useQueuedMessageStore.getState().holdAtFront(input.queueContext.threadKey, {
        ...taken,
        holdUntilUserAction: true,
      });
      input.setThreadError(threadId, describeSendFailure(error));
      return true;
    },
  };
}

/** Queues a busy draft or returns its send snapshot without mutating the composer. */
function prepareDraftSend<ComposerTarget>(
  input: ChatTurnSubmissionInput<ComposerTarget>,
): PreparedComposerSend | null {
  const sendContext = input.composerRef.current?.getSendContext();
  if (!sendContext) return null;
  const prompt = input.promptRef.current;
  const files = [...(input.readComposerDraft(input.composerDraftTarget)?.files ?? [])];
  const holdByTurn = input.isSendBusy || input.isConnecting || input.sendInFlightRef.current;
  if (holdByTurn && input.queueContext) {
    queueBusyDraft(input, {
      prompt,
      images: sendContext.images,
      files,
      terminalContexts: sendContext.terminalContexts,
    });
    return null;
  }
  if (holdByTurn) return null;
  return {
    prompt,
    images: sendContext.images,
    files,
    terminalContexts: sendContext.terminalContexts,
    restoreFailedQueueEntry: () => false,
  };
}

/** Moves a sendable running-turn draft into the queue, leaving blocked drafts intact. */
function queueBusyDraft<ComposerTarget>(
  input: ChatTurnSubmissionInput<ComposerTarget>,
  draft: Pick<PreparedComposerSend, "prompt" | "images" | "files" | "terminalContexts">,
): void {
  const context = input.queueContext;
  if (!context) return;
  const hasSendableDraft =
    draft.prompt.trim().length > 0 ||
    draft.images.length > 0 ||
    draft.files.length > 0 ||
    draft.terminalContexts.length > 0;
  const decision = decideQueuedTurnGate({
    phase: context.phase,
    isSendBusy: input.isSendBusy,
    isConnecting: input.isConnecting,
    sendInFlight: input.sendInFlightRef.current,
    hasSendableDraft,
    followUpQueueEnabled: context.followUpQueueEnabled,
  });
  if (decision !== "queue") return;
  useQueuedMessageStore.getState().enqueue(context.threadKey, {
    ...draft,
    queuedAfterToolActivityId: context.latestToolActivityId,
    createdAt: new Date().toISOString(),
  });
  input.promptRef.current = "";
  input.composerImagesRef.current = [];
  input.composerTerminalContextsRef.current = [];
  input.clearComposerDraftContent(input.composerDraftTarget);
}
